import { createApp } from 'vue'
import vuetify from '@/plugins/vuetify'
import BeautifulChat from 'vue3-beautiful-chat'
import App from '@/App.vue'
import router from '@/router'
import pinia from '@/store'
import logger from '@/logger.js'
import axios from 'axios'
import moment from 'moment-timezone'
import VueGtag from 'vue-gtag'
import AlbatrossTextField from '@/components/customVuetify/AlbatrossTextField.vue'
import AlbatrossTextarea from '@/components/customVuetify/AlbatrossTextarea.vue'
import AlbatrossButton from '@/components/customVuetify/AlbatrossButton.vue'
import AlbatrossSelect from '@/components/customVuetify/AlbatrossSelect.vue'
import AlbatrossAutocomplete from '@/components/customVuetify/AlbatrossAutocomplete.vue'

import '@/styles/main.scss'
import { requestInterceptor, responseInterceptor } from '@/helpers/interceptors'
import { useUserStore } from '@/stores/UserStore.js'
import { useScheduleStore } from '@/stores/ScheduleStore.js'

const { VITE_GA_ID, VITE_SENTRY_DSN } = import.meta.env

const app = createApp(App)
  .use(pinia)
  .use(vuetify)
  .use(router)
  .use(VueGtag, {
    config: { id: VITE_GA_ID }
  })
  //add the Albatross Components Globally so we can stop importing them everywhere
  .component('a-autocomplete', AlbatrossAutocomplete)
  .component('a-select', AlbatrossSelect)
  .component('a-text-field', AlbatrossTextField)
  .component('a-textarea', AlbatrossTextarea)
  .component('a-btn', AlbatrossButton)

app.config.productionTip = false

app.config.globalProperties.$filters = {
  currency(value, symbol, digits) {
    if (typeof value != 'number' || typeof digits != 'number') {
      return
    } else {
      return symbol + parseFloat(value).toFixed(digits)
    }
  },
  percent(value, digits = 0) {
    if (typeof value != 'number' || typeof digits != 'number') return

    value = value * 100

    let integer = parseInt(value)

    if (value === integer) return value + '%'
    else {
      return parseFloat(value).toFixed(digits) + '%'
    }
  },
  formatDateZonelessHHmm(value) {
    if (value) {
      return moment.utc(String(value), 'HH:mm').format('h:mm A')
    }
    return ''
  },
  //this filter is only used for the zoneless time picker stuff
  formatDateZoneless(value) {
    if (value) {
      return moment.utc(String(value), 'HH:mm:ss').format('h:mm a')
    }
  },
pluralize(amount, singularText, pluralText) {
    return amount === 1 ? singularText : pluralText
},
searchHighlight(value, query, ignoreWhiteSpace = false) {
    if (value) {
      // Escape special regex characters
      query = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const escapeRegex = (char) => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      if (ignoreWhiteSpace) {
        query = query.replace(/\s+/g, '');
      }

      const pattern = query
        .split('')
        .map(char => `\\s*${escapeRegex(char)}`)
        .join('');

      const regex = new RegExp(pattern, 'ig');

      return value.replace(regex, v => `<span class="highlight">${v}</span>`);
    }
  },
  fieldValues(field) {
    if (Array.isArray(field.values)) {
      return field?.values?.join(', ')
    }
    return field.values
  },
  formatDate(value, type, format, inputFormat) {
    /*
    //  this part of the code: `moment(String(value))` was throwing format warnings from moment with regular timestamp formats
    //  i can probably handle more scenarios but for now these don't throw errors: .format('YYYY-MM-DD') OR .format('YYYY-MM-DDTHH:mm:ssZ')
    //  TYPES: 'date', 'timestamp'
    */
    const userStore = useUserStore()
    const scheduleStore = useScheduleStore()

    let timezone = userStore.timezone.value

    //The schedule screen has it's own timezone. Use that if user is on schedule screen, else default to regular timezone
    if (
      router.currentRoute.value.name === 'schedule' &&
      scheduleStore.timezone?.value
    ) {
      timezone = scheduleStore.timezone.value
    }

    if (!type || (type === 'timestamp' && !timezone)) {
      console.error('TYPE IS REQUIRED, TIMEZONE IS REQUIRED FOR TIMESTAMPS')
      return
    }

    // default format if none provided
    if (!format) {
      // timestampAsDate means do the timezone conversion but then only display the date
      format =
        type === 'date' || type === 'timestampAsDate'
          ? 'M/D/YYYY'
          : 'M/D/YYYY h:mm a'
    }

    if (value) {
      // date doesn't do anything with timezone, just reformats the string
      // leave it be = assume it is already in the right timezone and dont mess with it
      return type === 'date'
        ? moment.utc(String(value), inputFormat ?? null).format(format)
        : moment
            .utc(String(value), inputFormat ?? null)
            .tz(timezone)
            .format(format)
    }
  }
}

axios.interceptors.request.use(requestInterceptor)
axios.interceptors.response.use((r) => r, responseInterceptor)

if (VITE_SENTRY_DSN) {
  logger(VITE_SENTRY_DSN, app, router)
}
app.use(BeautifulChat)
app.mount('#app')

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register(
    import.meta.env.MODE === 'production'
      ? '/firebase-messaging-sw.js'
      : '/dev-sw.js?dev-sw',
    { type: import.meta.env.MODE === 'production' ? 'classic' : 'module' }
  )
}
